import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Small, dependency-free client-side pagination control.
 *
 * Renders nothing when the full list already fits on a single page — this keeps
 * short lists visually clean while still giving the user page-size control on
 * bigger datasets.
 */
export default function Pagination({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sizes = [10, 25, 50, 100],
  testidPrefix = "pagination",
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const safePage = Math.min(page, totalPages);
  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-3 text-xs"
      data-testid={testidPrefix}
    >
      <div className="text-[#4B5563]">
        Showing <b>{(safePage - 1) * pageSize + 1}</b>–
        <b>{Math.min(safePage * pageSize, total)}</b> of{" "}
        <b>{total}</b>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <Label className="label-eyebrow !text-[10px]">Rows / page</Label>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="rounded-none h-9 w-20" data-testid={`${testidPrefix}-size`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sizes.map((n) => (
              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage <= 1}
          className="px-3 py-1.5 border border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0a0a0a] hover:text-white flex items-center gap-1"
          data-testid={`${testidPrefix}-prev`}
        >
          <ChevronLeft className="w-3 h-3" /> Prev
        </button>
        <span
          className="font-mono font-bold text-[#0a0a0a]"
          data-testid={`${testidPrefix}-indicator`}
        >
          {safePage} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
          disabled={safePage >= totalPages}
          className="px-3 py-1.5 border border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0a0a0a] hover:text-white flex items-center gap-1"
          data-testid={`${testidPrefix}-next`}
        >
          Next <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
